import * as React from 'react'

import { Theme, useTheme } from '@emotion/react'

import * as tokens from '@cash-design-system/tokens'

import { CashAppDollarLogo } from '../../icons/logos/CashAppDollarLogo'
import { CashAppDollarLogoWithText } from '../../icons/logos/CashAppDollarLogoWithText'
import { CashAppTaxesLogoWithText } from '../../icons/logos/CashAppTaxesLogoWithText'
import { ColorVariants } from './Logo'

export type CashAppLogoSvgProps = {
  altText?: string
  logoColor: string
  textColor?: string
  symbolColor: string
} & React.SVGAttributes<SVGElement>

export type Variants = ColorVariants | 'taxes'

export type LogoProps = {
  variant?: Variants
  withText?: boolean
} & Omit<CashAppLogoSvgProps, 'logoColor' | 'symbolColor' | 'textColor'>

type Color = string | { light: string; dark: string }
type LogoVariant = { logoColor: Color; textColor: Color; symbolColor: Color }

export const LOGO_VARIANTS: Record<Variants, LogoVariant> = {
  white: {
    logoColor: tokens.ColorPaletteWhite,
    textColor: tokens.ColorPaletteWhite,
    symbolColor: '',
  },
  black: {
    logoColor: tokens.ColorPaletteGray20,
    textColor: tokens.ColorPaletteGray20,
    symbolColor: '',
  },
  investingPurple: {
    logoColor: tokens.ColorPaletteInvestingPurpleLight,
    textColor: tokens.ColorPaletteInvestingPurpleLight,
    symbolColor: '',
  },
  green: {
    logoColor: tokens.ColorTintGreenLight,
    textColor: tokens.ColorTintGreenLight,
    symbolColor: '',
  },
  greenLogoWhiteText: {
    logoColor: tokens.ColorTintGreenLight,
    textColor: tokens.ColorPaletteWhite,
    symbolColor: '',
  },
  greenLogoBlackText: {
    logoColor: tokens.ColorTintGreenLight,
    textColor: tokens.ColorPaletteGray20,
    symbolColor: '',
  },
  cashAppCard: {
    logoColor: 'currentColor',
    textColor: 'currentColor',
    symbolColor: '',
  },
  taxes: {
    logoColor: { light: tokens.ColorPaletteBlack, dark: tokens.ColorPaletteWhite },
    textColor: { light: tokens.ColorPaletteBlack, dark: tokens.ColorPaletteWhite },
    symbolColor: '',
  },
  greenMonochrome: {
    logoColor: { light: tokens.ColorTintGreenLight, dark: tokens.ColorPaletteWhite },
    textColor: { light: tokens.ColorPaletteBlack, dark: tokens.ColorPaletteWhite },
    symbolColor: '',
  },
  default: {
    logoColor: tokens.ColorTintGreenLight,
    textColor: tokens.ColorPaletteBlack,
    symbolColor: tokens.ColorPaletteWhite,
  },
}

export const getThemedColorValue = (theme: Theme, color: Color): string => {
  if (typeof color === 'string') {
    return color
  }
  return theme.darkMode ? color.dark : color.light
}

const CashAppLogo = ({ altText, variant = 'default', withText, ...props }: LogoProps) => {
  const theme = useTheme()

  const LogoComponentWithText = variant === 'taxes' ? CashAppTaxesLogoWithText : CashAppDollarLogoWithText
  const LogoComponent = withText ? LogoComponentWithText : CashAppDollarLogo

  const { logoColor, textColor, symbolColor } = LOGO_VARIANTS[variant] || LOGO_VARIANTS.default

  return (
    <LogoComponent
      altText={altText}
      logoColor={getThemedColorValue(theme, logoColor)}
      symbolColor={getThemedColorValue(theme, symbolColor)}
      textColor={getThemedColorValue(theme, textColor)}
      {...props}
    />
  )
}

export default CashAppLogo
